import { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '@/stores/appStore';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Building2, 
  Globe, 
  Shield,
  Save,
  Database,
  Loader2,
  KeyRound,
  Copy,
  Lock,
  ArrowRight,
  Upload,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { currencies, languages, roleDisplayNames } from '@/types';
import { Link } from 'react-router-dom';

/** Plans that include the Member Portal / join-code feature */
const PORTAL_PLANS = ['intermediate', 'advanced'];

export default function Settings() {
  const { t, i18n } = useTranslation();
  const { currentOrganization, setCurrentOrganization, userRole } = useAppStore();
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('organization');
  
  // Organization settings
  const [orgName, setOrgName] = useState(currentOrganization?.name || '');
  const [orgCurrency, setOrgCurrency] = useState(currentOrganization?.currency || 'NGN');
  const [orgLanguage, setOrgLanguage] = useState(currentOrganization?.language || 'en');
  const [orgMonthlyDues, setOrgMonthlyDues] = useState(currentOrganization?.monthly_dues_amount?.toString() || '');
  const [orgAddress, setOrgAddress] = useState(currentOrganization?.address || '');
  const [orgPhone, setOrgPhone] = useState(currentOrganization?.phone || '');
  const [orgEmail, setOrgEmail] = useState(currentOrganization?.email || '');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  
  // Trust-but-verify mode
  const [trustVerifyMode, setTrustVerifyMode] = useState(true);

  const hasPortalAccess = PORTAL_PLANS.includes(
    currentOrganization?.subscription_plan ?? ''
  );
  const joinCode = currentOrganization?.join_code ?? null;

  useEffect(() => {
    setLogoPreview(null);
    setLogoFile(null);
  }, [currentOrganization?.id]);

  const handleCopyCode = () => {
    if (!joinCode) return;
    navigator.clipboard.writeText(joinCode).then(() => {
      toast.success('Join code copied to clipboard!');
    });
  };

  const handleSaveOrganization = async () => {
    if (!currentOrganization) return;

    setIsSubmitting(true);
    try {
      const { data, error } = await supabase
        .from('organizations')
        .update({
          name: orgName.trim(),
          currency: orgCurrency,
          language: orgLanguage,
          monthly_dues_amount: parseFloat(orgMonthlyDues) || 0,
          address: orgAddress || null,
          phone: orgPhone || null,
          email: orgEmail || null,
        })
        .eq('id', currentOrganization.id)
        .select()
        .single();

      if (error) throw error;

      setCurrentOrganization(data, userRole || 'admin');
      
      // Update language
      i18n.changeLanguage(orgLanguage);
      
      toast.success(t('organization.updateSuccess'));
    } catch (error) {
      console.error('Error updating organization:', error);
      toast.error(error instanceof Error ? error.message : t('errors.generic'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const isStorageConfigurationError = (message: string) => {
    const lowerMessage = message.toLowerCase();
    return (
      lowerMessage.includes('storage') ||
      lowerMessage.includes('bucket') ||
      lowerMessage.includes('not configured')
    );
  };

  const isLogoColumnMissingError = (message: string) => {
    const lowerMessage = message.toLowerCase();
    return (
      lowerMessage.includes('logo_url') &&
      (lowerMessage.includes('column') || lowerMessage.includes('schema cache'))
    );
  };

  const getSafeImageUrl = (url: string | null) => {
    if (!url) return null;
    return url.startsWith('blob:') ? url : null;
  };

  const handleLogoChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo must be under 2MB');
      return;
    }

    if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) {
      toast.error('Only JPEG, PNG, WebP, or GIF files are supported');
      return;
    }

    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleUploadLogo = async () => {
    if (!logoFile || !currentOrganization) return;

    setIsUploadingLogo(true);
    try {
      const fileExt = logoFile.name.split('.').pop() || logoFile.type.split('/')[1];
      if (!fileExt) {
        toast.error('Could not determine logo file extension.');
        return;
      }
      const filePath = `org-logos/${currentOrganization.id}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('organization-assets')
        .upload(filePath, logoFile, { upsert: true, contentType: logoFile.type });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from('organization-assets')
        .getPublicUrl(filePath);

      const logoUrl = urlData.publicUrl;

      const { data, error: updateError } = await supabase
        .from('organizations')
        .update({ logo_url: logoUrl })
        .eq('id', currentOrganization.id)
        .select()
        .single();

      if (updateError) {
        if (isLogoColumnMissingError(updateError.message)) {
          setCurrentOrganization({ ...currentOrganization, logo_url: logoUrl }, userRole || 'admin');
          setLogoFile(null);
          toast.error('Logo URL could not be saved — please run the database migration first.');
          return;
        }
        throw updateError;
      }

      setCurrentOrganization(data, userRole || 'admin');
      setLogoFile(null);
      toast.success('Logo updated successfully');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to upload logo';
      if (isStorageConfigurationError(message)) {
        toast.error('Storage not configured. Please contact support.');
      } else {
        toast.error(message);
      }
    } finally {
      setIsUploadingLogo(false);
    }
  };

  if (!currentOrganization) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">No organization selected</p>
      </div>
    );
  }

  const safeLogoPreviewUrl = getSafeImageUrl(logoPreview);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">{t('navigation.settings')}</h1>
        <p className="text-muted-foreground">Manage your organization settings</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="organization">
            <Building2 className="w-4 h-4 mr-2" />
            Organization
          </TabsTrigger>
          <TabsTrigger value="language">
            <Globe className="w-4 h-4 mr-2" />
            Language
          </TabsTrigger>
          <TabsTrigger value="access">
            <Shield className="w-4 h-4 mr-2" />
            Access
          </TabsTrigger>
        </TabsList>

        <TabsContent value="organization" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Organization Profile
              </CardTitle>
              <CardDescription>
                Update your organization's basic information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3 rounded-lg border p-4 bg-muted/20">
                <Label htmlFor="logo-upload">Organization Logo</Label>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-lg border bg-muted flex items-center justify-center overflow-hidden shrink-0">
                    {safeLogoPreviewUrl ? (
                      <img src={safeLogoPreviewUrl} alt="Organization logo preview" className="w-full h-full object-cover" />
                    ) : (
                      <Building2 className="w-6 h-6 text-muted-foreground" />
                    )}
                  </div>
                  <div className="space-y-2 flex-1">
                    <Input
                      id="logo-upload"
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={handleLogoChange}
                    />
                    <p className="text-xs text-muted-foreground">JPEG, PNG, WebP, or GIF. Max 2MB.</p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleUploadLogo}
                      disabled={!logoFile || isUploadingLogo}
                    >
                      {isUploadingLogo ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4 mr-2" />
                      )}
                      {isUploadingLogo ? 'Uploading...' : 'Upload Logo'}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Organization Name</Label>
                <Input
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  placeholder="Organization name"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Currency</Label>
                  <Select value={orgCurrency} onValueChange={setOrgCurrency}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {currencies.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.symbol} - {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Monthly Dues Amount</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={orgMonthlyDues}
                    onChange={(e) => setOrgMonthlyDues(e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Address</Label>
                <Input
                  value={orgAddress}
                  onChange={(e) => setOrgAddress(e.target.value)}
                  placeholder="Organization address"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={orgPhone}
                    onChange={(e) => setOrgPhone(e.target.value)}
                    placeholder="Phone number"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={orgEmail}
                    onChange={(e) => setOrgEmail(e.target.value)}
                    placeholder="Organization email"
                  />
                </div>
              </div>

              <Button 
                onClick={handleSaveOrganization}
                disabled={isSubmitting}
                className="w-full"
              >
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Changes
              </Button>
            </CardContent>
          </Card>

          {/* Member Portal Join Code — always visible to admins, gated by plan */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <KeyRound className="w-5 h-5" />
                Member Portal Join Code
              </CardTitle>
              <CardDescription>
                Share this code with members so they can access their records via the Member Portal.
              </CardDescription>
            </CardHeader>
            <Separator />
            <CardContent className="pt-4">
              {hasPortalAccess && joinCode ? (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Members use this code on the sign-up page to join your organisation and view their status.
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-muted rounded-lg px-4 py-3 text-center border border-border">
                      <span className="text-2xl font-mono font-bold tracking-widest text-primary select-all">
                        {joinCode}
                      </span>
                    </div>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleCopyCode}
                      title="Copy join code"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="secondary" className="text-xs">
                      {currentOrganization.subscription_plan === 'advanced' ? 'Advanced Plan' : 'Intermediate Plan'}
                    </Badge>
                    <span className="text-xs text-muted-foreground">Member Portal is active</span>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg border border-dashed border-border">
                    <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center shrink-0">
                      <Lock className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">Not Available (NA)</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        The Member Portal and join code are only available on the <strong>Intermediate</strong> plan and above.
                        Your current plan is <strong className="capitalize">{currentOrganization.subscription_plan ?? 'Free'}</strong>.
                      </p>
                    </div>
                  </div>
                  <Link to="/subscription">
                    <Button variant="outline" size="sm" className="w-full gap-1">
                      Upgrade to Unlock <ArrowRight className="w-3 h-3" />
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="language" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Globe className="w-5 h-5" />
                Language Settings
              </CardTitle>
              <CardDescription>
                Choose your preferred language for the app
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Language</Label>
                <Select value={orgLanguage} onValueChange={(v) => { setOrgLanguage(v); i18n.changeLanguage(v); }}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {languages.map((l) => (
                      <SelectItem key={l.code} value={l.code}>
                        {l.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button 
                onClick={handleSaveOrganization}
                disabled={isSubmitting}
                className="w-full"
              >
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Language Preference
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="access" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Member Access
              </CardTitle>
              <CardDescription>
                Control what members can see and do
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div>
                  <p className="font-medium">Trust-but-Verify Mode</p>
                  <p className="text-sm text-muted-foreground">
                    Allow members to view their own records (read-only)
                  </p>
                </div>
                <Switch
                  checked={trustVerifyMode}
                  onCheckedChange={setTrustVerifyMode}
                />
              </div>

              <div className="p-4 bg-muted rounded-lg">
                <p className="font-medium mb-2">User Roles</p>
                <div className="space-y-2">
                  {Object.entries(roleDisplayNames).map(([role, name]) => (
                    <div key={role} className="flex items-center justify-between text-sm">
                      <span>{name}</span>
                      <Badge variant={role === 'admin' ? 'default' : 'secondary'}>
                        {role}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Database className="w-5 h-5" />
                Data Management
              </CardTitle>
              <CardDescription>
                Backup and export your data
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button variant="outline" className="w-full">
                <Database className="w-4 h-4 mr-2" />
                Export All Data
              </Button>
              <Button variant="outline" className="w-full">
                <Database className="w-4 h-4 mr-2" />
                Backup to Cloud
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
